import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import MessageTemplateService from "../services/MessageTemplateService";

export default function QuotationSendDialog({
  open,
  onClose,
  onSend,
  quotation,
}) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setTemplateId("");
    setLoading(true);
    MessageTemplateService.getAllTemplates()
      .then((items) => {
        const approved = (items || []).filter((template) => {
          const status = String(template.getStatus?.() || "").toLowerCase();
          const metaId = template.getTemplateId?.();
          return status === "approved" && metaId;
        });
        setTemplates(approved);
      })
      .catch(() => {
        setTemplates([]);
        setError("Failed to load templates");
      })
      .finally(() => setLoading(false));
  }, [open]);

  const handleSend = async () => {
    if (!templateId) {
      setError("Select a template");
      return;
    }
    setSending(true);
    setError("");
    try {
      await onSend?.({ templateId });
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to send quotation");
    } finally {
      setSending(false);
    }
  };

  const label = quotation?.quotation_label
    || (quotation?.quotation_number ? `QUOT-${quotation.quotation_number}` : "quotation");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Quotation via WhatsApp</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Sends an approved WhatsApp template to
          {" "}
          <strong>{quotation?.contact_name || "the contact"}</strong>
          {" "}
          (
          {quotation?.contact_phone || "no phone"}
          ), then attaches the quotation PDF (
          {label}
          ).
        </Typography>
        <Alert severity="info">
          Template body variables auto-filled: customer name, quotation number, and total amount.
        </Alert>
        {loading ? (
          <CircularProgress size={24} sx={{ alignSelf: "center" }} />
        ) : (
          <FormControl fullWidth>
            <InputLabel>WhatsApp Template</InputLabel>
            <Select
              label="WhatsApp Template"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.map((template) => (
                <MenuItem key={template.getId()} value={template.getId()}>
                  {template.getTemplateName()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {!loading && templates.length === 0 && (
          <Alert severity="warning">
            No approved Meta templates found. Create and sync a template in Broadcast → Templates first.
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button variant="contained" onClick={handleSend} disabled={sending || !templateId}>
          {sending ? "Sending..." : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
